import { Fragment, type ReactNode } from "react";
import { Text } from "react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import type {
  PlanAction,
  PlanStep,
  StartTreatmentPresetCycleType,
} from "../plan.types";
import { useManualStepCompletion } from "../hooks/useManualStepCompletion";
import {
  PreCycleHeroCard,
  type PreCycleHeroSnapshotItem,
  type PreCyclePrepStep,
} from "./PreCycleHeroCard";
import { Stepper } from "./Stepper";

export type PreCycleHomeSection = {
  id: string;
  render: () => ReactNode;
  includeInPlanSetup?: boolean;
  progressStepIds?: string[];
  /**
   * StepperSection id this section's "Mark as done" persists under
   * (homepage_state.taskSections.completed). When set, it drives the hero
   * "N of M steps complete" badge; otherwise the legacy progressStepIds/status
   * path is used.
   */
  completionStepId?: string;
};

type Props = {
  activeStep: PlanStep | null;
  nextAction?: PlanAction;
  steps: PreCyclePrepStep[];
  completedSteps: number;
  totalSteps: number;
  eyebrow: string;
  title: string;
  snapshotItems: PreCycleHeroSnapshotItem[];
  phaseDescription: string;
  intro: string;
  sections: PreCycleHomeSection[];
  onAction: (action: PlanAction) => void;
  onOpenPlan: () => void;
  onEditPlan: () => void;
  showStartCycleAction?: boolean;
  startCyclePreset?: StartTreatmentPresetCycleType;
  openPlanAccessibilityLabel?: string;
  planAccessibilityLabel?: string;
};

export function PreCycleHome({
  activeStep,
  nextAction,
  steps,
  eyebrow,
  title,
  snapshotItems,
  phaseDescription,
  intro,
  sections,
  onAction,
  onOpenPlan,
  onEditPlan,
  showStartCycleAction,
  startCyclePreset,
  openPlanAccessibilityLabel,
  planAccessibilityLabel,
}: Props) {
  const { completed: completedPlanStepIds } = useManualStepCompletion();
  const planSetupSections = sections.filter(
    (section) => section.includeInPlanSetup !== false,
  );
  const planSetupTotalSteps = planSetupSections.length;
  const statusByStepId = new Map(
    steps.map((step) => [step.id, step.status] as const),
  );
  const planSetupCompletedSteps = planSetupSections.filter((section) => {
    // Preferred: the step card and plan action share this journey-scoped
    // completion ID, so the badge tracks the same persisted progress.
    if (section.completionStepId) {
      return completedPlanStepIds.includes(section.completionStepId);
    }
    const progressStepIds = section.progressStepIds ?? [section.id];
    if (progressStepIds.length === 0) return false;
    return progressStepIds.every(
      (stepId) => statusByStepId.get(stepId) === "complete",
    );
  }).length;

  return (
    <>
      <PreCycleHeroCard
        activeStep={activeStep}
        nextAction={nextAction}
        completedSteps={planSetupCompletedSteps}
        totalSteps={planSetupTotalSteps}
        eyebrow={eyebrow}
        title={title}
        snapshotItems={snapshotItems}
        phaseDescription={phaseDescription}
        openPlanAccessibilityLabel={openPlanAccessibilityLabel}
        planAccessibilityLabel={planAccessibilityLabel}
        onAction={onAction}
        onOpenPlan={onOpenPlan}
        onEditPlan={onEditPlan}
        showStartCycleAction={showStartCycleAction}
        startCyclePreset={startCyclePreset}
      />

      <Text
        className="font-dm-sans"
        style={{
          marginTop: 34,
          marginBottom: 18,
          paddingHorizontal: 4,
          fontSize: 18,
          lineHeight: 24,
          color: EMBER.colors.textLight,
          fontWeight: "700",
        }}
      >
        {intro}
      </Text>

      <Stepper>
        {sections.map((section) => (
          <Fragment key={section.id}>{section.render()}</Fragment>
        ))}
      </Stepper>
    </>
  );
}
